import { Flex, Separator, Text, HStack, Box, Wrap, WrapItem } from "@chakra-ui/react";
import { ApexOptions } from "apexcharts";
import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { AmountUtil } from "src/data/AmountUtil";
import { exchangeRateService } from "src/service/ExchangeRateService";
import CountryFlag from "./CountryFlag";
import { StatRoot, StatDownTrend, StatUpTrend } from "@/components/ui/stat"
import WaitingSpinner from "./WaitingSpinner";
import { useColorMode } from "./ui/color-mode";

export interface ExchangeRatesProps {
    base: string;
    all: Array<string>;
}

const ExchangeRates = (props: ExchangeRatesProps) => {

    const [data, setData] = useState<Map<string, number>>(new Map());
    const [history, setHistory] = useState<Map<string, Map<string, number>>>(new Map());
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        const fetchData = async () => {
            try {
                // execute sequentially, as the first call will fill the cache on the server side
                const historyResponse = props.all.reduce<Promise<Array<[string, Map<string, number>]>>>(
                    (previous, counter) => previous.then(entries =>
                        exchangeRateService.listHistory(props.base, counter)
                            .then(res => [...entries, [counter, new Map(Object.entries(res))]])
                    ),
                    Promise.resolve([])
                );
                const [ratesResponse, historyEntries] = await Promise.all([
                    exchangeRateService.listRates(props.base),
                    historyResponse
                ]);
                setData(new Map(Object.entries(ratesResponse.rates)));
                setHistory(new Map(historyEntries));
            } catch (error) {
                console.error('Error fetching exchange rates:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    return (
        <Wrap
            gap={{ base: 3, md: 4 }}
            justify='center'
            align='stretch'
            width='100%'
        >
            {props.all.map((counterCcy, ix) => {
                const rate = data.get(counterCcy) ?? 0;
                const aHistory: Map<string, number> = history.get(counterCcy) ?? new Map();
                const series = Array.from(aHistory).map(([name, value]: [string, number]) => {
                    let c = {
                        x: new Date(name).getTime(),
                        y: value
                    };
                    return c;
                });
                // calculate if increasing, if the last point is bigger than the previous from the series
                const decreasing = series.length > 1 && series[series.length - 1].y < series[series.length - 2].y;
                return (
                    <WrapItem
                        key={ix}
                        width={{ base: '100%', md: '350px' }}
                        maxW='full'
                        minW='0'
                    >
                        <Box
                            borderWidth='1px'
                            borderColor='border'
                            bg='bg.panel'
                            rounded='lg'
                            width='full'
                            minW='0'
                        >
                            <HStack justify={'space-between'} alignItems={"flex-end"} px='.5rem' wrap='wrap'>
                                <Flex wrap='wrap'>
                                    <CountryFlag code={props.base.substring(0, 2)} />
                                    <Text px='.4rem' fontWeight={'bold'} fontSize={'1.5rem'} >{props.base}/{counterCcy}</Text>
                                    <CountryFlag code={counterCcy.substring(0, 2)} />
                                </Flex>
                                {isLoading ?
                                    (<WaitingSpinner size='md' />)
                                    :
                                    (<HStack>
                                        <StatRoot>
                                            {decreasing ? <StatDownTrend /> : <StatUpTrend />}
                                        </StatRoot>
                                        <Text fontWeight={'extrabold'} fontSize={'1.5rem'} color={rate > 0 ? 'fg' : 'fg.error'}>{AmountUtil.format(rate)}</Text>
                                    </HStack>)
                                }

                            </HStack>
                            <Separator orientation='horizontal' p='0px' />
                            {series && (<HistoryChart data={series} />)}
                        </Box>
                    </WrapItem>
                )
            })}
        </Wrap>
    );
}

interface HistoryChartProps {
    data: Array<{
        x: number;
        y: number;
    }>;
}

const HistoryChart = ({ data }: HistoryChartProps) => {
    const { colorMode } = useColorMode();
    const height = 100;
    const options: ApexOptions = {
        chart: {
            background: 'transparent',
            id: "area-datetime",
            height: height,
            defaultLocale: 'en',
            toolbar: {
                show: false,
                tools: {
                    download: false,
                    selection: true,
                    zoom: false,
                    zoomin: true,
                    zoomout: true,
                    pan: false,
                    reset: false,
                },
            },
            zoom: {
                enabled: false,
            },
            dropShadow: {
                enabled: true,
                top: .5,
                left: 0,
                blur: 1,
                opacity: 0.5
            },
        },
        theme: {
            mode: colorMode,
        },
        legend: {
            show: false,
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            type: 'datetime',
            tickAmount: 6,
        },
        yaxis: {
            labels: {
                formatter: function (val: any) {
                    return AmountUtil.format(val);
                },
            }
        },
    };
    const series: NonNullable<ApexOptions["series"]> = [
        {
            name: 'Kurs',
            data: data
        }
    ]
    return (
        <div><ReactApexChart options={options} series={series} type="area" height={height} width='100%' /></div>
    )
}

export default ExchangeRates
